import { resetMedicationRequestStore, useMedicationRequestStore } from '@/lib/stores/medication-request'
import { useEffect, useMemo, useRef } from 'react'
import { Order } from './sections/previous-orders'

import { Checkbox } from '@/components/ui/checkbox'
import { CancelCircle } from '@/components/ui/icons/cancel-circle'
import { CheckmarkBadge } from '@/components/ui/icons/checkmark-badge'
import { Loading } from '@/components/ui/icons/loading'
import LoadingSpinner from '@/components/ui/icons/loading-spinner'
import { useToast } from '@/hooks/use-toast'
import { useCurrentMedication } from '@/hooks/useCurrentMedication'
import { medReqFieldValidationNotifications, getPatientAgeText, convertDateToISOWithoutConverting } from '@/lib/utils'
import { shouldLockMedRequest } from '@/lib/permissions'
import useMedRequestAction, { invalidateRelevantActionQueries } from '@/lib/api/med-requests/action'
import type { BatchMedRequest, Medication } from '@/types/data'
import { Column } from './sections/patient-data'
import { useGoNextMedRequest } from '@/hooks/useGoNextMedRequest'
import { useLockItemById } from '@/services/useLockItem'
import { ConsultationTable } from './consultation-table'
import { useQuestionnaire } from '@/hooks/useQuestionnaire'
import { format } from 'date-fns'
import { useGetMedications } from '@/services/medications'
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion'
import { AdditionalNotesEntries } from './sections/additional-notes'
import { usePosthogCustom } from '@/hooks/usePosthogCustom'

/**
 * Resolves the prescribed item name and directions for a batch order.
 *
 */
function resolvePrescribedItem(order: BatchMedRequest, medications: Medication[]) {
  const medication = medications.find(
    medication => medication.variantHash === order.orderedItem?.id || medication.productHash === order.orderedItem?.id
  )

  const name = medication?.productName || order.orderedItem?.name || ''
  const directions = order.orderedItem?.directions || medication?.medicationDirections || ''

  return { name, directions }
}

function BatchOrder({ order }: { order: BatchMedRequest }) {
  const { parsedQuestions: variableQuestions } = useQuestionnaire(order?.questionaire ?? '')

  const { data: medications = [] } = useGetMedications()
  const { name: prescribedItemName, directions: prescribedDirections } = resolvePrescribedItem(order, medications)

  useLockItemById(order.id, !order.locked && shouldLockMedRequest(order))

  const { setSelectedBatchOrderIds, selectedBatchOrderIds } = useMedicationRequestStore()

  const handleSelectOrder = (requestId: string) => {
    const isSelected = selectedBatchOrderIds.includes(requestId)
    if (isSelected) {
      setSelectedBatchOrderIds(selectedBatchOrderIds.filter((id: string) => id !== requestId))
    } else {
      setSelectedBatchOrderIds([...selectedBatchOrderIds, requestId])
    }
  }

  const { trackMedRequestBatchViewed } = usePosthogCustom()

  useEffect(() => {
    trackMedRequestBatchViewed(order.id)
  }, [])

  const patient = order.patient

  const patientAgeText = useMemo(() => getPatientAgeText(patient?.dob), [patient?.dob])

  const patientName = patient?.firstName ? `${patient?.firstName} ${patient?.lastName}` : '-'

  const isChecked = selectedBatchOrderIds.includes(order.id)
  const batchOrderData = useMedicationRequestStore(state => state.batchOrderData)

  return (
    <div className='flex flex-col w-full justify-between gap-6 self-stretch rounded-2xl border border-[#D1D9E2] mb-4 bg-white p-6 sm:p-8'>
      <div className='flex items-center gap-6'>
        <Checkbox checked={isChecked} onClick={() => handleSelectOrder(order.id)} className='w-8 h-8' />
        <span className='font-bold text-2xl'>{order.orderMedNameString.split(' ')?.[0]}</span>
      </div>

      <div className='flex flex-col gap-6 mt-6'>
        <p className='mb-6 text-custom-neutral-600 text-xl font-semibold leading-[133.4%] tracking-normal'>
          Medication Information
        </p>

        <div className='flex gap-10 px-8 py-6 rounded-2xl border border-[#E9EAED] bg-[#FAFAFB]'>
          <div className='flex flex-col gap-2 w-1/4'>
            <h3 className='font-bold text-base'>Prescribed Item</h3>
            <span className='text-base'>{prescribedItemName || 'N/A'}</span>
          </div>

          <div className='flex flex-col gap-2 w-full'>
            <h3 className='font-bold text-base'>Directions</h3>
            <span className='text-base'>{prescribedDirections || 'N/A'}</span>
          </div>
        </div>
      </div>

      <div className='w-full h-px bg-[#E9EAED] my-8' />

      <div className='flex flex-col gap-6'>
        <p className='mb-6 text-custom-neutral-600 text-xl font-semibold leading-[133.4%] tracking-normal'>
          Patient Details
        </p>
        <div className='flex h-full py-8 px-12 w-full justify-between items-center rounded-2xl border border-[#E9EAED]'>
          <Column header='Patient name' text={patientName} dataTestId='patient-name' />
          <div className='h-full w-px bg-custom-neutral-700' />
          <Column header='Age' text={patientAgeText} dataTestId='patient-age' />
          <div className='h-full w-px bg-custom-neutral-700' />
          <Column header='Gender' text={patient?.gender} dataTestId='patient-gender' />
          <div className='h-full w-px bg-custom-neutral-700' />
          <Column header='Postcode' text={order?.orderPostCode} dataTestId='patient-postcode' />
        </div>
      </div>

      <div className='w-full h-px bg-[#E9EAED] my-8' />

      <p className='text-custom-neutral-600 text-xl font-semibold leading-[133.4%] tracking-normal'>
        Previous 12 Weeks
      </p>

      {order.previousOrders.length > 0 ? (
        <div className='grid grid-cols-2 gap-4 max-h-[410px] overflow-y-auto'>
          {order.previousOrders.map(orderObj => (
            <Order key={orderObj.id} order={orderObj} />
          ))}
        </div>
      ) : (
        <p>No previous orders found</p>
      )}

      {!!variableQuestions.length && (
        <>
          <div className='w-full h-px bg-[#E9EAED] my-8' />

          <div className='flex flex-col'>
            <p className='text-custom-neutral-600 text-xl font-semibold leading-[133.4%] tracking-normal'>
              Variable Questions
            </p>
            <ConsultationTable data={variableQuestions} />
          </div>
        </>
      )}

      <div className='w-full h-px bg-[#E9EAED] my-8' />

      <Accordion type='single' collapsible className='border border-[#D1D9E2] rounded-3xl p-8'>
        <AccordionItem value='consultation-data' className='flex flex-col gap-6 border-0'>
          <AccordionTrigger className='hover:no-underline py-0'>
            <h2 className='text-custom-neutral-600 text-xl font-semibold leading-[133.4%]'>Additional Notes</h2>
          </AccordionTrigger>

          <AccordionContent>
            <AdditionalNotesEntries
              values={batchOrderData[order.id]?.notes}
              onChange={(e, entry) => {
                const { batchOrderData, setBatchOrderData } = useMedicationRequestStore.getState()
                setBatchOrderData({
                  ...batchOrderData,
                  [order.id]: {
                    ...batchOrderData[order.id],
                    notes: {
                      ...batchOrderData[order.id]?.notes,
                      [entry]: e.target.value
                    }
                  }
                })
              }}
            />
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  )
}

type OrderStatusHeaderProps = {
  status: 'approved' | 'rejected' | 'on-hold'
  orderMedNameString: string
}

function OrderStatusHeader({ status = 'approved', orderMedNameString }: OrderStatusHeaderProps) {
  const statusToPropsMap = {
    approved: {
      svg: <CheckmarkBadge />,
      text: 'The medication request has been approved'
    },
    rejected: {
      svg: <CancelCircle />,
      text: 'The medication request has been rejected'
    },
    'on-hold': {
      svg: <Loading />,
      text: 'The medication request has been placed on hold'
    }
  }

  const props = statusToPropsMap[status]

  return (
    <div className='flex flex-col justify-center items-center'>
      <p className='mb-6'>{props.svg}</p>
      <p className='text-[#24272B] text-center text-2xl font-semibold leading-[150%] tracking-[0.15px]'>{props.text}</p>
      <p className='text-[#24272B] text-center text-base font-normal leading-[150%] tracking-[0.15px]'>
        {orderMedNameString}
      </p>
    </div>
  )
}

const PostOrderView = () => {
  const { toast } = useToast()
  const {
    status,
    setStatus,
    medication,
    medicationIssueDate,
    medicationDirection,
    batchOrders,
    selectedBatchOrderIds
  } = useMedicationRequestStore()

  const { trackBatchApproval } = usePosthogCustom()
  const sessionStartTime = useRef(new Date())

  const medRequest = useCurrentMedication()
  const goNextMedRequest = useGoNextMedRequest()

  const isLookingForNewOrders = ['on-hold', 'rejected'].includes(status)
  const isApproveSelectedDisabled = selectedBatchOrderIds.length === 0

  const { mutateAsync } = useMedRequestAction({ isBatch: true })
  const { data: medications = [] } = useGetMedications()

  if (!medRequest) return null
  const { data } = medRequest

  const handleBatchApproval = async () => {
    if (!data) return
    if (selectedBatchOrderIds.length === 0) return
    if (!medReqFieldValidationNotifications('approve')) return

    const { batchOrderData, justification } = useMedicationRequestStore.getState()

    const batchOrdersToApprove = batchOrders.filter(batchOrder => selectedBatchOrderIds.includes(batchOrder.id))

    const promises = batchOrdersToApprove.map(batchOrder => {
      const { directions } = resolvePrescribedItem(batchOrder, medications)
      return mutateAsync({
        id: batchOrder.id,
        action: 'approve',
        reason: justification,
        approveDate: convertDateToISOWithoutConverting(medicationIssueDate),
        extraDirectionsForMedication: directions,
        additionalNotes: batchOrderData[batchOrder.id]?.notes
      })
    })

    for (const id of selectedBatchOrderIds) {
      trackBatchApproval(id, sessionStartTime.current)
    }

    await Promise.all(promises)
    invalidateRelevantActionQueries(data.patient.id, data.id)

    resetMedicationRequestStore()
    toast({
      title: 'Success',
      description: 'Medication requests succesfully approved.'
    })

    goNextMedRequest()
  }

  if (!data) return null

  return (
    <div className='max-w-[1375px] w-full mx-auto'>
      <div className='mb-10 flex px-[88px] py-8 flex-col justify-center items-center rounded-3xl border border-[#E9EAED] bg-[#FAFAFB]'>
        <OrderStatusHeader status='approved' orderMedNameString={data.orderMedNameString} />
        <div className='w-full h-px bg-[#E9EAED] my-8' />
        <div className='flex  py-8 px-2 justify-between items-start rounded-2xl'>
          <div className='shrink-0 pr-10'>
            <p className='mb-2 text-[#1D1D1D] text-base font-bold leading-[170%]'>Prescribed item</p>
            <p className='text-custom-neutral-800 text-base font-normal leading-[170%]'>{medication?.productName}</p>
          </div>
          <div className='px-10 border-l border-r border-[#E9EAED]'>
            <p className='mb-2  text-[#1D1D1D]  text-base font-bold leading-[170%]'>Directions</p>
            <p className='text-custom-neutral-800 text-base font-normal leading-[170%]'>{medicationDirection}</p>
          </div>
          <div className='shrink-0 pl-10'>
            <p className='mb-2  text-[#1D1D1D] text-base font-bold leading-[170%]'>Issue Date</p>
            <p className='text-custom-neutral-800 text-base font-normal leading-[170%]'>
              {format(medicationIssueDate, 'dd MMMM yyyy')}
            </p>
          </div>
        </div>
      </div>
      {status === 'approved' && (
        <>
          <p className='mb-6 text-custom-neutral-600 text-xl font-semibold leading-[133.4%] tracking-normal'>
            Batch Approve Orders
          </p>
          {batchOrders.map(order => (
            <BatchOrder key={order.id} order={order} />
          ))}
          <div className='fixed left-0 bottom-0 flex w-full h-[88px] flex-col justify-center items-center shrink-0 border-t border-[#FAFAFB] bg-white shadow-[0px_-8px_80px_0px_rgba(0,0,0,0.10)]'>
            <div className='flex justify-center gap-4 w-full max-w-[994px]'>
              <button
                onClick={handleBatchApproval}
                type='button'
                className={`flex w-[200px] h-14 flex-col justify-center items-center gap-2 rounded-2xl py-4 px-6 transition-colors duration-200 ${
                  isApproveSelectedDisabled ? 'bg-gray-400 cursor-not-allowed' : 'bg-[#153CB7] hover:bg-[#0F2E8A]'
                }`}
                disabled={isApproveSelectedDisabled}
              >
                <span className='text-white text-center text-sm font-semibold leading-[170%]'>Approve Selected</span>
              </button>
              <button
                onClick={() => {
                  setStatus('pending')
                  resetMedicationRequestStore()
                  goNextMedRequest()
                }}
                type='button'
                className={
                  'bg-white text-slate-700 text-sm font-semibold flex w-[200px] h-14 flex-col justify-center items-center gap-2 rounded-2xl py-4 px-6 transition-colors duration-200 border border-gray-300'
                }
              >
                Skip to Next
              </button>
            </div>
          </div>
        </>
      )}
      {isLookingForNewOrders && (
        <div className='grid mt-[160px] place-items-center'>
          <LoadingSpinner />
          <p className='text-[#393E45] text-2xl font-semibold leading-[150%] tracking-[0.15px] mt-8'>
            We're moving on to the next request.
          </p>
        </div>
      )}
    </div>
  )
}

export default PostOrderView
